import React, { useState, useRef, useEffect } from 'react';
import './RegisterModal.css';
import { API_URL as API } from '../services/api';

const INITIAL_FORM = { full_name: '', email: '', org_name: '', password: '', confirm: '' };

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters',    ok: password.length >= 8 },
    { label: 'Uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', ok: /[a-z]/.test(password) },
    { label: 'Number',           ok: /\d/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const bar = ['', 'rm-str-weak', 'rm-str-fair', 'rm-str-good', 'rm-str-strong'][score];
  if (!password) return null;
  return (
    <div className="rm-strength">
      <div className="rm-strength-bar">
        {[0,1,2,3].map(i => (
          <div key={i} className={`rm-strength-seg ${i < score ? bar : ''}`} />
        ))}
      </div>
      <div className="rm-strength-checks">
        {checks.map(c => (
          <span key={c.label} className={`rm-strength-check ${c.ok ? 'rm-check-ok' : ''}`}>
            {c.ok ? '✓' : '○'} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function OtpInput({ value, onChange, disabled }) {
  const r0 = useRef(null), r1 = useRef(null), r2 = useRef(null);
  const r3 = useRef(null), r4 = useRef(null), r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];

  const handleKey = (e, i) => {
    if (e.key === 'Backspace') {
      if (value[i]) {
        const next = value.split('');
        next[i] = '';
        onChange(next.join(''));
      } else if (i > 0) {
        refs[i - 1].current?.focus();
      }
    }
  };

  const handleChange = (e, i) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[i] = ch;
    onChange(next.join(''));
    if (ch && i < 5) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    refs[focusIdx].current?.focus();
  };

  return (
    <div className="rm-otp-row">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={`rm-otp-box ${value[i] ? 'rm-otp-filled' : ''}`}
          value={value[i] || ''}
          onChange={e => handleChange(e, i)}
          onKeyDown={e => handleKey(e, i)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

export default function RegisterModal({ onClose, onLoginRedirect }) {
  const [step, setStep]           = useState(1); // 1=form, 2=otp, 3=success
  const [form, setForm]           = useState(INITIAL_FORM);
  const [showPw, setShowPw]       = useState(false);
  const [otp, setOtp]             = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [agreed, setAgreed]       = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const validateForm = () => {
    if (!form.full_name.trim()) return 'Full name is required.';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return 'Valid email is required.';
    if (!form.org_name.trim()) return 'Organization name is required.';
    if (form.password.length < 8)           return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(form.password))       return 'Password must contain an uppercase letter.';
    if (!/[a-z]/.test(form.password))       return 'Password must contain a lowercase letter.';
    if (!/\d/.test(form.password))          return 'Password must contain a number.';
    if (form.password !== form.confirm)     return 'Passwords do not match.';
    return '';
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setError(err); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email:     form.email.trim().toLowerCase(),
          org_name:  form.org_name.trim(),
          password:  form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Registration failed.'); return; }
      setStep(2);
      setResendCooldown(60);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { setError('Enter all 6 digits.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim().toLowerCase(), code: otp }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Verification failed.'); return; }
      setStep(3);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true); setError(''); setOtp('');
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email:     form.email.trim().toLowerCase(),
          org_name:  form.org_name.trim(),
          password:  form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to resend.'); return; }
      setResendCooldown(60);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="rm-modal">

        {/* Header */}
        <div className="rm-header">
          <div className="rm-header-left">
            <div className="rm-header-icon">
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="#C8102E" fillOpacity=".15" stroke="#C8102E" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M9 22V12h6v10" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h2 className="rm-title">Create your account</h2>
              <p className="rm-subtitle">
                {step === 1 && 'Set up your property management workspace'}
                {step === 2 && `Code sent to ${form.email}`}
                {step === 3 && 'Welcome to whereitis!'}
              </p>
            </div>
          </div>
          <button className="rm-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Steps indicator */}
        {step < 3 && (
          <div className="rm-steps">
            {['Your details', 'Verify email', 'Done'].map((label, i) => (
              <React.Fragment key={label}>
                <div className={`rm-step-dot ${step > i + 1 ? 'rm-dot-done' : step === i + 1 ? 'rm-dot-active' : ''}`}>
                  {step > i + 1
                    ? <svg viewBox="0 0 16 16" fill="none" width="10" height="10"><polyline points="3 8 7 12 13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    : i + 1
                  }
                </div>
                <span className={`rm-step-label ${step === i + 1 ? 'rm-label-active' : ''}`}>{label}</span>
                {i < 2 && <div className={`rm-step-line ${step > i + 1 ? 'rm-line-done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rm-error">
            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
              <circle cx="10" cy="10" r="9" stroke="#C8102E" strokeWidth="1.5"/>
              <line x1="10" y1="6" x2="10" y2="11" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
              <circle cx="10" cy="14" r="1" fill="#C8102E"/>
            </svg>
            {error}
          </div>
        )}

        {/* ── Step 1: Registration form ── */}
        {step === 1 && (
          <form className="rm-form" onSubmit={handleRegister} noValidate>
            <div className="rm-field">
              <label className="rm-label">Full Name</label>
              <input className="rm-input" type="text" placeholder="John Smith"
                value={form.full_name} onChange={e => setField('full_name', e.target.value)} autoFocus />
            </div>

            <div className="rm-field">
              <label className="rm-label">Email Address</label>
              <input className="rm-input" type="email" placeholder="john@example.com"
                value={form.email} onChange={e => setField('email', e.target.value)} />
            </div>

            <div className="rm-field">
              <label className="rm-label">Organization Name</label>
              <input className="rm-input" type="text" placeholder="Sunset Properties Ltd."
                value={form.org_name} onChange={e => setField('org_name', e.target.value)} />
              <p className="rm-hint">Your organization will be created automatically. You can add team members later.</p>
            </div>

            <div className="rm-field-row">
              <div className="rm-field">
                <label className="rm-label">Password</label>
                <div className="rm-pw-wrap">
                  <input className="rm-input" type={showPw ? 'text' : 'password'}
                    placeholder="••••••••" value={form.password}
                    onChange={e => setField('password', e.target.value)} />
                  <button type="button" className="rm-pw-toggle" onClick={() => setShowPw(v => !v)}>
                    {showPw
                      ? <svg viewBox="0 0 20 20" fill="none" width="15" height="15"><path d="M3 3l14 14M12.45 12.45A3 3 0 017.55 7.55M8.7 5.1A8.7 8.7 0 0110 5c5 0 8 5 8 5a15.3 15.3 0 01-2.7 3.3M6.5 6.5A14.5 14.5 0 002 10s3 5 8 5a8.6 8.6 0 004.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      : <svg viewBox="0 0 20 20" fill="none" width="15" height="15"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                    }
                  </button>
                </div>
                <PasswordStrength password={form.password} />
              </div>
              <div className="rm-field">
                <label className="rm-label">Confirm Password</label>
                <input className="rm-input" type={showPw ? 'text' : 'password'}
                  placeholder="••••••••" value={form.confirm}
                  onChange={e => setField('confirm', e.target.value)} />
                {form.confirm && (
                  <p className={`rm-match-hint ${form.password === form.confirm ? 'rm-match-ok' : 'rm-match-no'}`}>
                    {form.password === form.confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>
            </div>

            {/* ── User Agreement ── */}
            <div className="rm-agreement">
              <label className="rm-agree-check">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                <span>
                  I have read and agree to the{' '}
                  <button type="button" className="rm-link" onClick={() => setShowTerms(v => !v)}>
                    Terms of Service & Privacy Policy
                  </button>
                </span>
              </label>

              {showTerms && (
                <div className="rm-terms-box">
                  <p className="rm-terms-heading">Terms of Service &amp; Privacy Policy</p>

                  <p className="rm-terms-section">1. Acceptance of Terms</p>
                  <p>By creating an account on <strong>whereitis</strong>, you agree to these Terms of Service. If you do not agree, do not register.</p>

                  <p className="rm-terms-section">2. Account Responsibilities</p>
                  <p>You are responsible for maintaining the confidentiality of your login credentials. You must not share your account with others. You must provide accurate information during registration.</p>

                  <p className="rm-terms-section">3. Permitted Use</p>
                  <p>whereitis is a property management platform for Airbnb hosts and property managers. You may use the platform to manage guidebooks, respond to guest queries, and oversee your properties. Commercial resale or misuse of the platform is prohibited.</p>

                  <p className="rm-terms-section">4. Data & Privacy</p>
                  <p>We collect your name, email, and organization details to provide the service. Your data is stored securely and is never sold to third parties. Guest interaction data (chat sessions) is retained to help you manage queries. You may request deletion of your account and data at any time by contacting support.</p>

                  <p className="rm-terms-section">5. Content</p>
                  <p>You retain ownership of content you upload (guidebook text, property details). You grant whereitis a licence to store and process that content solely to deliver the service.</p>

                  <p className="rm-terms-section">6. Termination</p>
                  <p>We reserve the right to suspend or terminate accounts that violate these terms or misuse the platform.</p>

                  <p className="rm-terms-section">7. Limitation of Liability</p>
                  <p>whereitis is provided "as is". We are not liable for any indirect or consequential losses arising from use of the platform.</p>

                  <p className="rm-terms-section">8. Changes</p>
                  <p>We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance.</p>

                  <p style={{marginTop: '10px', fontSize: '0.72rem', color: '#9aaabf'}}>Last updated: June 2026</p>
                </div>
              )}
            </div>

            <button type="submit" className="rm-btn-primary" disabled={loading || !agreed}>
              {loading
                ? <span className="rm-spinner" />
                : 'Create Account & Send Code'
              }
            </button>

            <p className="rm-signin-link">
              Already have an account?{' '}
              <button type="button" className="rm-link" onClick={onLoginRedirect}>Sign in</button>
            </p>
          </form>
        )}

        {/* ── Step 2: OTP verification ── */}
        {step === 2 && (
          <form className="rm-form" onSubmit={handleVerify} noValidate>
            <div className="rm-otp-section">
              <div className="rm-otp-icon">
                <svg viewBox="0 0 48 48" fill="none" width="52" height="52">
                  <rect x="4" y="8" width="40" height="32" rx="4" fill="#EEF1F7" stroke="#1A2744" strokeWidth="2"/>
                  <path d="M4 16l20 13 20-13" stroke="#C8102E" strokeWidth="2"/>
                </svg>
              </div>
              <p className="rm-otp-desc">
                We sent a 6-digit code to <strong>{form.email}</strong>.
                Enter it below to verify your email address.
              </p>
            </div>

            <OtpInput value={otp} onChange={setOtp} disabled={loading} />

            <button type="submit" className="rm-btn-primary" disabled={loading || otp.length < 6}>
              {loading ? <span className="rm-spinner" /> : 'Verify Email'}
            </button>

            <div className="rm-resend">
              <span>Didn't receive the code?</span>
              {resendCooldown > 0
                ? <span className="rm-resend-wait">Resend in {resendCooldown}s</span>
                : <button type="button" className="rm-link" onClick={handleResend} disabled={loading}>Resend code</button>
              }
            </div>

            <button type="button" className="rm-back" onClick={() => { setStep(1); setOtp(''); setError(''); }}>
              ← Back to details
            </button>
          </form>
        )}

        {/* ── Step 3: Success ── */}
        {step === 3 && (
          <div className="rm-success">
            <div className="rm-success-icon">
              <svg viewBox="0 0 64 64" fill="none" width="72" height="72">
                <circle cx="32" cy="32" r="30" fill="rgba(200,16,46,0.06)" stroke="#C8102E" strokeWidth="2"/>
                <polyline points="18 32 27 42 46 22" stroke="#C8102E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="rm-success-title">You're all set!</h3>
            <p className="rm-success-body">
              Your admin account has been created and your organization
              <strong> "{form.org_name}"</strong> is ready to go.<br />
              Sign in with your <strong>email</strong> as the username on the Admin login screen.
            </p>
            <button className="rm-btn-primary" onClick={onLoginRedirect}>
              Go to Admin Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
